import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BudgetVarianceWidget = ({ data = [] }) => {
  const [sortBy, setSortBy] = useState('variance'); // variance, percentage, category

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(Math.abs(value));
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value?.toFixed(1)}%`;
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-success';
    if (variance < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getVarianceBackground = (variance) => {
    if (variance > 0) return 'bg-success/10';
    if (variance < 0) return 'bg-error/10';
    return 'bg-muted/10';
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return 'TrendingUp';
    if (variance < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getSortedData = () => {
    const sortedData = [...data];
    switch (sortBy) {
      case 'percentage':
        return sortedData?.sort((a, b) => Math.abs(b?.percentage) - Math.abs(a?.percentage));
      case 'category':
        return sortedData?.sort((a, b) => a?.category?.localeCompare(b?.category));
      default:
        return sortedData?.sort((a, b) => Math.abs(b?.variance) - Math.abs(a?.variance));
    }
  };

  const getTotalBudget = () => {
    return data?.reduce((sum, item) => sum + item?.budget, 0);
  };

  const getTotalActual = () => {
    return data?.reduce((sum, item) => sum + item?.actual, 0);
  };

  const getTotalVariance = () => {
    return data?.reduce((sum, item) => sum + item?.variance, 0);
  };

  const getVariancePercentage = () => {
    const totalBudget = getTotalBudget();
    return totalBudget > 0 ? (getTotalVariance() / totalBudget) * 100 : 0;
  };

  const sortedData = getSortedData();

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="Target" size={20} className="text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Budget Variance Analysis</h3>
            <p className="text-sm text-muted-foreground">Budget vs actual spending by category</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setSortBy('variance')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                sortBy === 'variance' 
                  ? 'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Variance
            </button>
            <button
              onClick={() => setSortBy('percentage')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                sortBy === 'percentage' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              %
            </button>
            <button
              onClick={() => setSortBy('category')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                sortBy === 'category' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              A-Z
            </button>
          </div>
          <Button variant="ghost" size="sm" iconName="AlertTriangle">
            Alerts
          </Button>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Budget</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(getTotalBudget())}
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Actual</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(getTotalActual())}
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Variance</div>
          <div className={`text-lg font-semibold ${getVarianceColor(getTotalVariance())}`}>
            {getTotalVariance() > 0 ? '+' : ''}{formatCurrency(getTotalVariance())}
            <span className="text-sm ml-1">
              ({formatPercentage(getVariancePercentage())})
            </span>
          </div>
        </div>
      </div>
      {/* Budget Items */}
      <div className="space-y-3">
        {sortedData?.map((item, index) => (
          <div key={index} className="bg-muted/20 rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getVarianceBackground(item?.variance)}`}>
                  <Icon 
                    name={getVarianceIcon(item?.variance)} 
                    size={16} 
                    className={getVarianceColor(item?.variance)}
                  />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">{item?.category}</h4>
                  <div className="text-xs text-muted-foreground">
                    Budget: {formatCurrency(item?.budget)} • Actual: {formatCurrency(item?.actual)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-medium ${getVarianceColor(item?.variance)}`}>
                  {item?.variance > 0 ? '+' : ''}{formatCurrency(item?.variance)}
                </div>
                <div className={`text-xs ${getVarianceColor(item?.variance)}`}>
                  {formatPercentage(item?.percentage)}
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="relative">
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    item?.actual <= item?.budget ? 'bg-success' : 'bg-error'
                  }`}
                  style={{ width: `${Math.min((item?.actual / item?.budget) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0%</span>
                <span className="text-foreground font-medium">
                  {((item?.actual / item?.budget) * 100)?.toFixed(1)}% of budget
                </span>
                <span>100%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Action Buttons */}
      <div className="flex items-center space-x-2 mt-6 pt-4 border-t border-border">
        <Button variant="outline" size="sm" iconName="FileText" className="flex-1">
          Generate Report
        </Button>
        <Button variant="outline" size="sm" iconName="Bell" className="flex-1">
          Set Alerts
        </Button>
        <Button variant="default" size="sm" iconName="TrendingUp" className="flex-1">
          Forecast
        </Button>
      </div>
    </div>
  );
};

export default BudgetVarianceWidget;